import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { ArrowBack, Delete, Add, Menu } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiURL } from "../constant";
const LessonCreator = () => {
  const navigate = useNavigate();
  const [lessonName, setLessonName] = useState("");
  const [words, setWords] = useState([
    { word: "", meaning: "", pronunciation: "", type: "" },
  ]);
  const [batchInputOpen, setBatchInputOpen] = useState(false);
  const [batchInput, setBatchInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleAddWord = () => {
    setWords([
      ...words,
      { word: "", meaning: "", pronunciation: "", type: "" },
    ]);
  };

  const handleRemoveWord = (index) => {
    if (words.length > 1) {
      setWords(words.filter((_, i) => i !== index));
    }
  };

  const handleUpdateWord = (index, field, value) => {
    const newWords = [...words];
    newWords[index] = { ...newWords[index], [field]: value };
    setWords(newWords);
  };

  const fetchPronunciation = async (word) => {
    try {
      const response = await fetch(`${apiURL}/api/audio?word=${word}`);
      const data = await response.json();
      return {
        ipa: data.ipa || "",
        audioUrl: data.audioUrl || "",
      };
    } catch (error) {
      console.error("Error fetching pronunciation:", error);
      return {
        ipa: "",
        audioUrl: "",
      };
    }
  };

  const handleSave = async () => {
    if (lessonName.trim() && words.length > 0) {
      try {
        setIsSaving(true);
        const validWords = words.filter(
          (word) => word.word.trim() && word.meaning.trim()
        );
        if (validWords.length === 0) return;

        // Fetch pronunciation for each word
        const wordsWithPronunciation = await Promise.all(
          validWords.map(async (word) => {
            const pronunciationData = await fetchPronunciation(word.word);
            return {
              ...word,
              pronunciation: pronunciationData?.ipa || word.pronunciation || "",
              audioUrl: pronunciationData?.audioUrl || word.audioUrl || "",
            };
          })
        );

        const savedLessons = JSON.parse(
          localStorage.getItem("lessons") || "[]"
        );
        const newLesson = {
          id: Date.now().toString(),
          name: lessonName.trim(),
          words: wordsWithPronunciation,
          createdAt: new Date().toISOString(),
        };
        savedLessons.push(newLesson);
        localStorage.setItem("lessons", JSON.stringify(savedLessons));
        navigate("/");
      } catch (error) {
        console.error("Error saving lesson:", error);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleBatchInput = () => {
    const lines = batchInput.split("\n").filter((line) => line.trim());
    const newWords = lines
      .map((line) => {
        // Format: từ (loại từ) nghĩa /phiên âm/
        const firstBracketIndex = line.indexOf("(");
        const lastBracketIndex = line.indexOf(")");
        const lastSlashIndex = line.lastIndexOf("/");

        if (
          firstBracketIndex !== -1 &&
          lastBracketIndex !== -1 &&
          lastSlashIndex !== -1
        ) {
          const word = line.substring(0, firstBracketIndex).trim();
          const type = line
            .substring(firstBracketIndex + 1, lastBracketIndex)
            .trim();

          // Tìm vị trí của dấu / đầu tiên sau dấu ngoặc đơn
          let firstSlashIndex = line.indexOf("/", lastBracketIndex);
          if (firstSlashIndex === -1) return null;

          // Lấy phần nghĩa từ sau dấu ngoặc đơn đến trước dấu /
          const meaning = line
            .substring(lastBracketIndex + 1, firstSlashIndex)
            .trim();
          const pronunciation = line
            .substring(firstSlashIndex + 1, lastSlashIndex)
            .trim();

          return {
            word: word,
            type: type,
            meaning: meaning,
            pronunciation: pronunciation,
            audioUrl: "",
          };
        }
        return null;
      })
      .filter((word) => word !== null);

    if (newWords.length > 0) {
      setWords((prevWords) => {
        const lastEmptyWord = prevWords[prevWords.length - 1];
        const hasEmptyLastWord = !lastEmptyWord.word && !lastEmptyWord.meaning;

        if (hasEmptyLastWord) {
          return [...prevWords.slice(0, -1), ...newWords, lastEmptyWord];
        }
        return [
          ...prevWords,
          ...newWords,
          { word: "", meaning: "", pronunciation: "", type: "" },
        ];
      });
    }

    setBatchInput("");
    setBatchInputOpen(false);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton onClick={() => navigate("/")} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Tạo bài học mới
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Menu />}
            onClick={() => setBatchInputOpen(true)}
            sx={{ mr: 2 }}
          >
            Thêm hàng loạt
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!lessonName.trim() || words.length === 0 || isSaving}
            startIcon={
              isSaving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isSaving ? "Đang tạo..." : "Lưu bài học"}
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 4 }}>
          <TextField
            fullWidth
            label="Tên bài học"
            value={lessonName}
            onChange={(e) => setLessonName(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Danh sách từ vựng ({words.length})
          </Typography>

          <Grid container spacing={2}>
            {words.map((word, index) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Từ vựng"
                        value={word.word}
                        onChange={(e) =>
                          handleUpdateWord(index, "word", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Nghĩa"
                        value={word.meaning}
                        onChange={(e) =>
                          handleUpdateWord(index, "meaning", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Phát âm"
                        value={word.pronunciation}
                        onChange={(e) =>
                          handleUpdateWord(
                            index,
                            "pronunciation",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="Loại từ"
                        value={word.type}
                        onChange={(e) =>
                          handleUpdateWord(index, "type", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => handleRemoveWord(index)}
                        color="error"
                        disabled={words.length === 1}
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddWord}
            fullWidth
            sx={{ mt: 2 }}
          >
            Thêm từ
          </Button>
        </Paper>

        <Dialog
          open={batchInputOpen}
          onClose={() => setBatchInputOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Thêm từ vựng hàng loạt</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Nhập từ vựng theo định dạng: từ (loại từ) nghĩa /phiên âm/
              <br />
              Ví dụ: Also (adv): cũng, ngoài ra /ˈɔːl.soʊ/
              <br />
              Mỗi từ trên một dòng
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={batchInput}
              onChange={(e) => setBatchInput(e.target.value)}
              placeholder="Also (adv): cũng, ngoài ra /ˈɔːl.soʊ/
Hopeful (adj): hy vọng /ˈhoʊp.fəl/
Serious (adj): nghiêm trọng, nghiêm túc /ˈsɪr.i.əs/"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBatchInputOpen(false)}>Hủy</Button>
            <Button onClick={handleBatchInput} variant="contained">
              Thêm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default LessonCreator;
